import "@/instrument"; // must be first — sets up Sentry before any component renders
import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// Silence the benign "ResizeObserver loop completed with undelivered notifications"
// warning that Recharts' ResponsiveContainer occasionally emits — this warning is
// harmless but its dev-time overlay blocks manual QA clicks. Only the message
// matches; every other error bubbles as usual.
if (typeof window !== "undefined") {
  const RE_RESIZE_LOOP = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
  window.addEventListener("error", (e) => {
    if (e && e.message && RE_RESIZE_LOOP.test(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    const msg = (e && e.reason && (e.reason.message || String(e.reason))) || "";
    if (RE_RESIZE_LOOP.test(msg)) e.preventDefault();
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

function SentryFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0b] text-white p-6">
      <div className="max-w-md text-center space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-red-300">Something went wrong</div>
        <div className="text-lg font-semibold">A rendering error was caught and reported.</div>
        <div className="text-sm text-zinc-400">
          Our team has been notified. Please refresh the page — if it keeps happening, drop us a note in the support portal.
        </div>
        <button
          onClick={() => window.location.reload()}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 px-4 py-2 text-sm font-medium"
          data-testid="sentry-reload-btn"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// Register the service worker so Web Push notifications can fire even when
// the app tab is closed. Fails open on unsupported browsers.
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").catch(() => { /* noop */ });
  });
}
